// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Snowflake, PenLine } from 'lucide-react'
import * as api from '../../api/client'
import { useAuth } from '../../auth/AuthContext'
import { carrierLabel } from '../../lib/carriers'

/**
 * Compact "Deliveries" strip on the patient detail page (Sprint 18).
 * Shows this patient's shipments — Rx-linked (via rx_item_id) AND
 * directly linked (via patient_id, e.g. imported shipments without an Rx).
 * Permission-gated (shipping.read).
 *
 * Compliance: status + tracking + drug only — no delivery address (that
 * lives on the Rx detail / ship drawer).
 */
/** How many deliveries the strip renders. It is a summary on a detail page, not a list page —
 *  the heading still shows the true total, and Shipments is where you go to see them all. */
const STRIP_ROWS = 25

export default function PatientShipmentsStrip({ patientId }: { patientId: string; rxIds: string[] }) {
  const { hasPermission } = useAuth()
  const navigate = useNavigate()
  const [items, setItems] = useState<api.ShipmentSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!hasPermission('shipping.read')) { setLoaded(true); return }
    // P1.2 (e2e pagination): filter server-side by patient. The old client filter ran over
    // the capped 1,000-newest list — on ASP (~13.9k shipments) a patient's older deliveries
    // silently vanished and the "Deliveries (N)" count was wrong. The server predicate also
    // matches co-shipped junction members, so bulk packages appear for every member patient.
    // rxIds is no longer needed for matching; kept in props for call-site compatibility.
    //
    // D-RXFILTER-4: EXPLICITLY BOUNDED. This used to call the unpaginated list and render
    // however many rows came back — a strip with no limit of its own, relying on a server cap
    // it never named. It asks for a page now, and the server returns the exact total, so the
    // heading counts every delivery this patient has while the strip renders a readable slice.
    api.listShipmentsPaged({ patientId, page: 1, pageSize: STRIP_ROWS })
      .then(res => { setItems(res.items); setTotal(typeof res.total === 'number' ? res.total : res.items.length) })
      .catch(() => { /* silent — strip just won't show */ })
      .finally(() => setLoaded(true))
  }, [hasPermission, patientId])

  if (!loaded || !hasPermission('shipping.read') || items.length === 0) return null

  return (
    <>
      <h2 style={sectionH2}>Deliveries ({total})</h2>
      <div style={cardStyle}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <tbody>
            {items.map(s => (
              <tr key={s.id} style={{ cursor: s.rx_item_id ? 'pointer' : 'default' }}
                  onClick={() => s.rx_item_id ? navigate(`/rx/${s.rx_item_id}`) : null}>
                <td style={td}>
                  {s.drug_name || s.rx_number || (s.imported_from_courier_at ? `Order ${s.carrier_shipment_id || s.tracking_number || ''}` : 'Rx')}
                </td>
                <td style={td}>{carrierLabel(s.carrier)}</td>
                <td style={td}>
                  {s.cold_chain && <Snowflake size={12} style={{ color: '#0369a1', marginRight: 4 }} />}
                  {s.require_signature && <PenLine size={12} style={{ color: '#7c3aed' }} />}
                </td>
                <td style={td}>{s.tracking_number || '—'}</td>
                <td style={{ ...td, textAlign: 'right' }}><StatusPill status={s.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Never a silent trim: if the patient has more deliveries than the strip renders, say so
          and point at the list that can show them all. */}
      {total > items.length && (
        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 6 }}>
          Showing the {items.length} most recent of {total}. Open Shipments and filter by this
          patient to see the rest.
        </div>
      )}
    </>
  )
}

function StatusPill({ status }: { status: string }) {
  const tone = ({ created: '#6b7280', label_purchased: '#0369a1', in_transit: '#0369a1',
    out_for_delivery: '#a16207', delivered: '#15803d', exception: '#dc2626', cancelled: '#6b7280' } as Record<string, string>)[status] ?? '#6b7280'
  const label = ({ created: 'Created', label_purchased: 'Label purchased', in_transit: 'In transit',
    out_for_delivery: 'Out for delivery', delivered: 'Delivered', exception: 'Exception', cancelled: 'Cancelled' } as Record<string, string>)[status] ?? status
  return <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, border: '1px solid', background: `${tone}1a`, color: tone, borderColor: `${tone}55` }}>{label}</span>
}
const sectionH2: React.CSSProperties = { fontSize: 15, fontWeight: 600, margin: '20px 0 8px' }
const cardStyle: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }
const td: React.CSSProperties = { padding: '8px 12px', borderBottom: '1px solid var(--color-border)' }
